<template>
  <div
    class="new-year-countdown-app"
    :style="{
      backgroundColor: transparentBackground ? 'transparent' : backgroundColor,
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: computedTextColor,
      fontFamily: computedFontFamily
    }"
  >
    <div class="main-text">{{ mainText }}</div>
    <div class="countdown-timer">
      <div class="time-unit">
        <span class="value">{{ days }}</span>
        <span class="label">Days</span>
      </div>
      <div class="time-unit">
        <span class="value">{{ hours }}</span>
        <span class="label">Hours</span>
      </div>
      <div class="time-unit">
        <span class="value">{{ minutes }}</span>
        <span class="label">Minutes</span>
      </div>
      <div class="time-unit">
        <span class="value">{{ seconds }}</span>
        <span class="label">Seconds</span>
      </div>
    </div>
    <div v-if="particleConfig.enabled" class="particle-effect-container" :class="particleConfig.type">
      <div v-for="i in 50" :key="i" class="particle" :class="`particle-${i}`"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'countdownApp',
  props: {
    mainText: {
      type: String,
      default: 'New Year'
    },
    targetDate: {
      type: String,
      default: ''
    },
    backgroundColor: {
      type: String,
      default: '#36476b'
    },
    transparentBackground: {
      type: Boolean,
      default: false
    },
    textStyle: {
      type: Object,
      default: () => ({
        fontFamily: 'Arial',
        color: '#FFFFFF'
      })
    },
    backgroundImage: {
      type: String,
      default: null
    },
    particleConfig: {
      type: Object,
      default: () => ({
        enabled: false,
        type: 'off',
        direction: 'top-bottom',
        zIndex: 1
      })
    }
  },
  data() {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      countdownInterval: null,
    };
  },
  computed: {
    computedFontFamily() {
      if (!this.textStyle || !this.textStyle.fontFamily) {
        return 'Arial, sans-serif';
      }
      return this.textStyle.fontFamily;
    },
    computedTextColor() {
      if (!this.textStyle || !this.textStyle.color) {
        return '#FFFFFF';
      }
      return this.textStyle.color;
    }
  },
  mounted() {
    this.startCountdown();
  },
  beforeUnmount() {
    clearInterval(this.countdownInterval);
  },
  watch: {
    targetDate: {
      immediate: true,
      handler(newDate) {
        console.log('Target date changed:', newDate); // Debug log
        this.startCountdown();
      }
    }
  },
  methods: {
    startCountdown() {
      clearInterval(this.countdownInterval);

      // Check if we have a valid target date
      if (!this.targetDate) {
        this.resetCountdown();
        return;
      }

      const updateCountdown = () => {
        try {
          const targetDate = new Date(this.targetDate);
          
          // Validate the date
          if (isNaN(targetDate.getTime())) {
            this.resetCountdown();
            return;
          }

          const now = new Date().getTime();
          const distance = targetDate.getTime() - now;

          if (distance < 0) {
            this.resetCountdown();
            clearInterval(this.countdownInterval);
            return;
          }

          // Calculate time components
          this.days = Math.floor(distance / (1000 * 60 * 60 * 24));
          this.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          this.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          this.seconds = Math.floor((distance % (1000 * 60)) / 1000);
        } catch (error) {
          console.error('Error updating countdown:', error);
          this.resetCountdown();
        }
      };

      // Initial update
      updateCountdown();
      
      // Set up interval for updates
      this.countdownInterval = setInterval(updateCountdown, 1000);
    },

    resetCountdown() {
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
    },
  },
};
</script>

<style scoped>
.new-year-countdown-app {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
  text-align: center;
  transition: background-color 0.3s ease, color 0.3s ease, font-family 0.3s ease;
  position: relative; /* For particle effects */
  overflow: hidden; /* For particle effects */
}

.main-text {
  font-size: 3em;
  font-weight: bold;
  margin-bottom: 20px;
  color: inherit; /* Use the color from parent */
  font-family: inherit; /* Use the font from parent */
}

.countdown-timer {
  display: flex;
  gap: 30px;
}

.time-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-unit .value {
  font-size: 2.5em;
  font-weight: bold;
  color: inherit; /* Use the color from parent */
  font-family: inherit; /* Use the font from parent */
}

.time-unit .label {
  font-size: 0.9em;
  text-transform: uppercase;
  margin-top: 5px;
  color: inherit; /* Use the color from parent */
  font-family: inherit; /* Use the font from parent */;
}

.particle-effect-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* Allows clicks to pass through */
  z-index: 0; /* Ensures particles are behind text */
}

.stars-effect {
  background-color: rgba(255, 255, 0, 0.2);
  animation: twinkle 5s infinite alternate;
  width: 100%;
  height: 100%;
}

.snow-effect {
  background-color: rgba(255, 255, 255, 0.3);
  animation: melt 8s infinite alternate;
  width: 100%;
  height: 100%;
}

@keyframes twinkle {
  0% { opacity: 0.5; }
  100% { opacity: 1; }
}

@keyframes melt {
  0% { transform: translateY(0); }
  100% { transform: translateY(100%); }
}
</style>